import FileUpload from "../../../components/common/FileUpload";
import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import ToggleSwitch from "../../../components/ToggleSwitch";
import SubHeadings from "../../../components/SubHeadings";
import { AddStudentForm, Student } from "../../../types/studentType";
import { useFormEdit } from "../../../util/hooks/useFormEdit";

import ClassSelect from "../../../components/common-input/ClassSelect";
import { useEffect, useState } from "react";
import StudentApis from "../../../api/studentApis";
import { toast } from "sonner";
import SubButton from "../../../components/SubButtons";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { BiLockAlt } from "react-icons/bi";
import UpdatePassword from "../../../components/modal/UpdatePassword";

interface Props {
  form: ReturnType<typeof useFormEdit<Student>>;
}

export default function ProfileInformation({ form }: Props) {
  const { formData, setFormData, isEditing } = form;
  const [logoChanged, setLogoChanged] = useState(false);
  const [profileUrl, setProfileUrl] = useState(formData.photoUrl ?? "");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const user = useSelector((state: RootState) => state.access.staff);

  const isAdmin =
    user?.roleName === "ROLE_ADMIN" || user?.roleName === "ROLE_SUPER_ADMIN";

  useEffect(() => {
    if (formData.photoUrl) {
      setProfileUrl(formData.photoUrl);
    }
  }, [formData.photoUrl]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoUpload = (url: string) => {
    const cleanUrl = url.replace(/^"+|"+$/g, "");

    setProfileUrl(cleanUrl);
    setLogoChanged(true);

    setFormData((prev) => ({
      ...prev,
      photoUrl: cleanUrl,
    }));
  };

  const handleSaveLogo = async () => {
    if (!formData?.admissionNumber || !profileUrl) return;

    try {
      await StudentApis.changeStudentProfile(
        formData.admissionNumber,
        profileUrl,
      );
      toast.success("Image updated successfully");
      setLogoChanged(false);
    } catch {
      toast.error("Failed to update Image");
    }
  };

  return (
    <div className="px-4  md:px-9 flex flex-col-reverse md:flex-row md:divide-x divide-neutral-50 gap-9">
      <div className="flex flex-col gap-7 w-full py-7">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:items-center">
          <SubHeadings text="Profile Information" />
          {/* <div className="flex gap-3 justify-end items-end">
            <label className="flex text-sm font-bold text-neutral-400 items-center">
              Opt for daycare?
            </label>
            <ToggleSwitch defaultChecked={formData.optForDaycare ?? false} disabled={!isEditing} />
          </div> */}
        </div>
        <form className="grid gap-7">
          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="fullName"
              label="Student name"
              readOnly={!isEditing}
              type="text"
              placeholder="Enter student name"
              value={formData.fullName ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="admissionNumber"
              label="Admission number (auto-generated)"
              type="text"
              readOnly
              placeholder="Enter Admission number"
              value={formData.admissionNumber?.toString() ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="dateOfBirth"
              label="Date of birth"
              type="date"
              readOnly={!isEditing}
              value={formData.dateOfBirth ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <ClassSelect
              name="classId"
              label="Class"
              readOnly={!isEditing}
              value={formData?.classId}
              placeholder="Select Class"
              onChange={handleInputChange}
            />
          </div>

          <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center w-full">
            <div className="flex gap-7">
              <label className="text-sm font-semibold text-neutral-400 block">
                Gender
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={formData.gender === "MALE"}
                    value="male"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.gender === "MALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Male
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={formData.gender === "FEMALE"}
                    value="female"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.gender === "FEMALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Female
                  </span>
                </div>
              </div>
            </div>

            {/* <div className="flex justify-end items-end relative group">
              <CustomButton
                color="bg-primary-500"
                text="Submit"
                disabled={!hasUnsavedChanges || !isEditing}
              />

              {!hasUnsavedChanges && (
                <span className="absolute bottom-full mb-2 bg-neutral-300 text-white text-xs font-medium px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 md:whitespace-nowrap">
                  {isEditing
                    ? "No changes yet — fill out or edit the form to get started"
                    : "Enable edit mode to make changes"}
                </span>
              )}
            </div> */}
          </div>
          <div className="w-[60%]">
            <CustomSelect
              name="studentCategory"
              label="Student Category"
              value={formData?.studentCategory}
              readOnly={!isEditing}
              options={[
                { label: "General", value: "GENERAL" },
                { label: "OBC", value: "OBC" },
                { label: "SC", value: "SC" },
                { label: "ST", value: "ST" },
              ]}
              placeholder="Select student category"
              onChange={handleInputChange}
            />
          </div>
        </form>
      </div>

      <div className="md:pl-7 pt-7 md:py-7">
        <FileUpload
          value={profileUrl}
          onUploadComplete={handleLogoUpload}
          className="md:w-52 w-36 aspect-square"
          disabled={!isEditing}
          showButton={!logoChanged}
          buttonText="Upload Image"
          uploadText="Change Image"
        />

        {isEditing && logoChanged && (
          <button
            type="button"
            onClick={handleSaveLogo}
            className="bg-primary-500 text-white rounded-lg py-2 px-4 w-full text-sm font-semibold mt-3"
          >
            Save Image
          </button>
        )}

        {isAdmin && (
          <div className="mt-5 p-4 rounded-2xl bg-background-100 border border-background-300 shadow-sm">
            <div className="flex flex-col items-center justify-between gap-3">
              {/* Left Side */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500">
                  <BiLockAlt />
                </div>

                <div className="text-center">
                  <p className="text-sm font-semibold text-neutral-500">
                    Password
                  </p>
                  <p className="text-xs text-neutral-300">
                    reset login credentials
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <SubButton
                text="Change"
                type="button"
                color="bg-primary-500"
                padding="px-6"
                disabled={!isEditing}
                onClick={() => setShowPasswordModal(true)}
              />
            </div>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <UpdatePassword
          onClose={() => setShowPasswordModal(false)}
          userId={formData?.userId}
        />
      )}
    </div>
  );
}
